// src/features/vita/chat/VitaChatScreens.jsx
//
// Telas secundárias do chat: perfil do VITA, mídias e configurações.
// Todas compartilham o mesmo cabeçalho de "voltar" e vivem dentro do MESMO
// offcanvas do chat — navegar aqui não empilha rota, só troca a tela.

import React, { useEffect, useMemo, useState } from "react";
import S from "../../../styles/features/vita/VitaChat.module.css";
import avatarVita from "../../../Assets/images/logo/avatar-vita.webp";
import {
  FONTES, TAMANHOS, TEMAS, IDIOMAS,
  fontePorId, tamanhoPorId, idiomaLabel,
} from "./vitaPrefs";
import {
  Apple, Bell, Camera, Check, ChevronLeft, ChevronRight, Download, Dumbbell,
  FileText, Flame, Globe, Images, Mic, Moon, Salad, Send, Settings, ShieldCheck,
  Trash2, TrendingUp, Type, X,
} from "./vitaIcons";

/**
 * Avatar do VITA.
 *
 * Importado de `src/Assets/images/logo/avatar-vita.webp` — o webpack resolve em
 * build time e versiona o arquivo com hash, então o cache do WebView não serve
 * uma arte antiga depois de uma troca.
 *
 * A silhueta em SVG fica como fallback do `onError`. Com import versionado ela
 * praticamente não dispara, mas é o que impede um avatar corrompido de virar um
 * buraco no cabeçalho, no FAB e no perfil de uma vez só.
 */
export function VitaAvatar({ tamanho = 42, borda = 2 }) {
  const [falhou, setFalhou] = useState(false);
  const icone = Math.round(tamanho * 0.52);

  return (
    <div
      className={S.avatar}
      style={{ width: tamanho, height: tamanho, borderWidth: borda }}
      aria-hidden="true"
    >
      {falhou ? (
        <svg width={icone} height={icone} viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="6.6" r="4.1" />
          <rect x="4" y="10.8" width="16" height="4.4" rx="2.2" />
          <path
            d="M10.5 15.4c1.5 1.2 1.5 2.4 0 3.6M13.5 15.4c-1.5 1.2-1.5 2.4 0 3.6"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
          />
        </svg>
      ) : (
        <img
          className={S.avatarImg}
          src={avatarVita}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFalhou(true)}
        />
      )}
    </div>
  );
}

function Cabecalho({ titulo, onVoltar }) {
  return (
    <div className={S.telaHeader}>
      <button type="button" className={S.voltarBtn} onClick={onVoltar} aria-label="Voltar">
        <ChevronLeft size={18} />
      </button>
      <span className={S.telaTitulo}>{titulo}</span>
    </div>
  );
}

function Toggle({ ligado, onMudar, rotulo }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      aria-label={rotulo}
      className={ligado ? S.toggleLigado : S.toggle}
      onClick={() => onMudar(!ligado)}
    >
      <span className={ligado ? S.toggleKnobLigado : S.toggleKnob} />
    </button>
  );
}

// ── Perfil do VITA ───────────────────────────────────────────────────────────

const HABILIDADES = [
  { Icone: Dumbbell, label: "Montar e ajustar seu treino" },
  { Icone: Salad, label: "Montar sua dieta" },
  { Icone: Camera, label: "Analisar refeição por foto" },
  { Icone: Check, label: "Registrar check-in de treino" },
  { Icone: Apple, label: "Somar as calorias e macros do dia" },
  { Icone: Mic, label: "Entender áudios que você mandar" },
  { Icone: TrendingUp, label: "Acompanhar sua evolução" },
  { Icone: Flame, label: "Cuidar da sua ofensiva e do seu XP" },
  { Icone: Bell, label: "Lembrar você de treinar" },
  { Icone: Send, label: "Conversar também pelo WhatsApp" },
];

export function VitaPerfil({ onVoltar, onAbrirMidias, onAbrirConfig, totalMidias }) {
  return (
    <>
      <Cabecalho titulo="Perfil do assistente" onVoltar={onVoltar} />
      <div className={S.tela}>
        <div className={S.perfilTopo}>
          <VitaAvatar tamanho={112} borda={3} />
          <div className={S.perfilNome}>VITA</div>
          <div className={S.perfilSub}>Assistente de saúde e treino da LesVita</div>
        </div>

        <div className={S.blocoTexto}>
          Ele conversa com você no dia a dia para montar treinos e dietas, registrar
          seus check-ins e acompanhar sua evolução — no seu ritmo, sem cobrança.
        </div>

        <div className={S.bloco}>
          <button type="button" className={S.item} onClick={onAbrirMidias}>
            <span className={S.itemIcone}><Images size={15} /></span>
            <span className={S.itemLabel}>Mídias</span>
            <span className={S.itemValor}>{totalMidias}</span>
            <ChevronRight size={16} color="#6b7285" />
          </button>
          <button type="button" className={S.item} onClick={onAbrirConfig}>
            <span className={S.itemIcone}><Settings size={15} /></span>
            <span className={S.itemLabel}>Configurações do chat</span>
            <ChevronRight size={16} color="#6b7285" />
          </button>
        </div>

        <div className={S.bloco}>
          <div className={S.blocoTitulo}>O QUE O VITA PODE FAZER</div>
          {HABILIDADES.map(({ Icone, label }) => (
            <div key={label} className={S.item} style={{ cursor: "default" }}>
              <span className={S.itemIconeOuro}><Icone size={15} /></span>
              <span className={S.itemLabel}>{label}</span>
            </div>
          ))}
        </div>

        <div className={S.privacidade}>
          <div className={S.privacidadeTitulo}>
            <ShieldCheck size={15} /> Segurança e privacidade
          </div>
          <div className={S.privacidadeTexto}>
            Sua conversa é protegida e seus dados são tratados conforme a LGPD.
            O VITA orienta e motiva, mas não substitui um profissional de saúde.
          </div>
        </div>
      </div>
    </>
  );
}

// ── Mídias ───────────────────────────────────────────────────────────────────

const MESES = ["JANEIRO", "FEVEREIRO", "MARÇO", "ABRIL", "MAIO", "JUNHO", "JULHO",
  "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO"];

const ABAS = [
  { chave: "foto", rotulo: "Fotos" },
  { chave: "audio", rotulo: "Áudios" },
  { chave: "documento", rotulo: "Documentos" },
];

const VAZIO = {
  foto: { Icone: Camera, titulo: "Nenhuma foto por aqui",
    texto: "Mande a foto de uma refeição ou do seu treino — o VITA analisa na hora." },
  audio: { Icone: Mic, titulo: "Nenhum áudio por aqui",
    texto: "Grave um áudio contando como foi o treino — fica mais rápido que digitar." },
  documento: { Icone: FileText, titulo: "Nenhum documento por aqui",
    texto: "Exames e laudos que você enviar para o VITA aparecem nesta lista." },
};

export function VitaMidias({ onVoltar, mensagens, onIrParaMensagem }) {
  const [aba, setAba] = useState("foto");
  const [aberta, setAberta] = useState(null);

  const grupos = useMemo(() => {
    const itens = mensagens.filter((m) => m.tipo === aba && m.midiaUrl);
    const mapa = new Map();
    itens.forEach((m) => {
      const d = new Date((m.ts || 0) * 1000);
      const chave = `${MESES[d.getMonth()]} DE ${d.getFullYear()}`;
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave).push(m);
    });
    return [...mapa.entries()];
  }, [mensagens, aba]);

  const conteudoVazio = VAZIO[aba];

  return (
    <>
      <Cabecalho titulo="Mídias" onVoltar={onVoltar} />
      <div className={S.abas} role="tablist">
        {ABAS.map((a) => (
          <button
            key={a.chave}
            type="button"
            role="tab"
            aria-selected={aba === a.chave}
            className={aba === a.chave ? S.abaAtiva : S.aba}
            onClick={() => setAba(a.chave)}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      {grupos.length === 0 ? (
        <div className={S.vazioMidia}>
          <span className={S.vazioMidiaIcone}><conteudoVazio.Icone size={30} /></span>
          <div className={S.vazioMidiaTitulo}>{conteudoVazio.titulo}</div>
          <div className={S.vazioMidiaTexto}>{conteudoVazio.texto}</div>
        </div>
      ) : (
        <div className={S.tela}>
          {grupos.map(([mes, itens]) => (
            <div key={mes}>
              <div className={S.grupoMes}>{mes}</div>
              <div className={S.grade}>
                {itens.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={S.gradeItem}
                    onClick={() => setAberta(m)}
                    aria-label="Abrir mídia"
                    style={m.midiaUrl ? { backgroundImage: `url(${m.midiaUrl})`, backgroundSize: "cover" } : undefined}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {aberta && (
        <div className={S.visualizador}>
          <div className={S.visualizadorHeader}>
            <button
              type="button"
              className={S.visualizadorFechar}
              onClick={() => setAberta(null)}
              aria-label="Fechar"
            >
              <X size={18} />
            </button>
            <div style={{ flex: 1 }}>
              <div className={S.visualizadorNome}>
                {aberta.papel === "user" ? "Você" : "VITA"}
              </div>
              <div className={S.visualizadorData}>
                {new Date((aberta.ts || 0) * 1000).toLocaleString("pt-BR")}
              </div>
            </div>
          </div>
          <div className={S.visualizadorPalco}>
            {aberta.midiaUrl && (
              <img className={S.visualizadorImagem} src={aberta.midiaUrl} alt="" />
            )}
          </div>
          <div className={S.visualizadorRodape}>
            <button
              type="button"
              className={S.visualizadorBtn}
              onClick={() => {
                setAberta(null);
                onIrParaMensagem?.(aberta);
              }}
            >
              Ir para a mensagem
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Configurações ────────────────────────────────────────────────────────────

// ── Modais da tela de configurações ──────────────────────────────────────────

/**
 * Folha inferior genérica. Todos os ajustes que precisam de escolha usam esta
 * mesma casca — um seletor inline por linha deixaria a tela alta e cada ajuste
 * com um comportamento diferente do vizinho.
 */
function ModalFolha({ titulo, descricao, children, onFechar, acoes }) {
  useEffect(() => {
    const aoTeclar = (e) => e.key === "Escape" && onFechar?.();
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [onFechar]);

  return (
    <div
      className={S.confirmacaoFundo}
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      onClick={onFechar}
    >
      {/* stopPropagation: clique no backdrop fecha, clique no cartão não. */}
      <div className={S.confirmacaoCard} onClick={(e) => e.stopPropagation()}>
        <div className={S.confirmacaoTitulo}>{titulo}</div>
        {descricao && <div className={S.confirmacaoTexto}>{descricao}</div>}
        {children}
        {acoes}
      </div>
    </div>
  );
}

/** Lista de opções exclusivas, com marca no item corrente. */
function ListaOpcoes({ opcoes, valor, onEscolher, estiloDoItem }) {
  return (
    <div className={S.opcaoLista} role="radiogroup">
      {opcoes.map((o) => {
        const ativa = o.id === valor;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={ativa}
            className={ativa ? S.opcaoAtiva : S.opcao}
            style={estiloDoItem ? estiloDoItem(o) : undefined}
            onClick={() => onEscolher(o.id)}
          >
            <span className={S.opcaoLabel}>{o.label}</span>
            {ativa && <Check size={17} />}
          </button>
        );
      })}
    </div>
  );
}

// ── Tela "Uso" (limites e consumo do período) ───────────────────────────────

export function VitaUso({ onVoltar, onEvoluirPlano }) {
  const [Comp, setComp] = useState(null);
  useEffect(() => {
    let vivo = true;
    import("../uso/UsoVita").then((m) => { if (vivo) setComp(() => m.default); });
    return () => { vivo = false; };
  }, []);
  return (
    <>
      <Cabecalho titulo="Uso" onVoltar={onVoltar} />
      <div className={S.tela}>
        {Comp ? <Comp onEvoluirPlano={onEvoluirPlano} /> : null}
      </div>
    </>
  );
}

export function VitaConfiguracoes({
  onVoltar, meta, onSalvar, onLimpar, onExportar, prefs, onPrefsChange,
  onAbrirUso,
}) {
  // Um modal por vez: null | "silencio" | "idioma" | "fontes" | "exportar" | "limpar"
  const [modal, setModal] = useState(null);
  const fechar = () => setModal(null);

  // Permissão de push do SO. Quando negada, esta tela é o caminho de VOLTA:
  // o app não fica reperguntando (ver PUSH_PROMPT no FCMService) — o usuário
  // reativa aqui, por gesto explícito.
  const [pushPerm, setPushPerm] = useState(null);
  useEffect(() => {
    let vivo = true;
    import("../../../services/notifications/FCMService")
      .then(({ FCMService }) => FCMService.permissionState())
      .then((s) => { if (vivo) setPushPerm(s); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  const reativarPush = async () => {
    try {
      const { FCMService } = await import("../../../services/notifications/FCMService");
      const res = await FCMService.requestFromSettings();
      setPushPerm(res === "granted" ? "granted" : res);
    } catch { /* estado permanece; nada a fazer aqui */ }
  };

  const notificacoes = meta.NOTIFICACOES !== false;
  const transcrever = meta.TRANSCREVER_AUDIOS !== false;
  const silencioAtivo = meta.SILENCIO_ATIVO === true;
  const inicio = meta.SILENCIO_INICIO || "22:00";
  const fim = meta.SILENCIO_FIM || "07:00";
  const idioma = meta.IDIOMA || "pt-BR";

  const fonte = fontePorId(prefs.fonte);
  const tamanho = tamanhoPorId(prefs.tamanho);

  const mudarPref = (patch) => onPrefsChange?.({ ...prefs, ...patch });

  return (
    <>
      <Cabecalho titulo="Configurações do chat" onVoltar={onVoltar} />
      <div className={S.tela}>
        {/* ── Uso (limites e consumo do período) ───────────────────────── */}
        {onAbrirUso && (
          <div className={S.bloco}>
            <button type="button" className={S.item} onClick={onAbrirUso}>
              <span className={S.itemIconeOuro}><TrendingUp size={15} /></span>
              <div className={S.itemLabel}>
                <div>Uso</div>
                <div className={S.itemSub}>Limites e consumo do período</div>
              </div>
              <ChevronRight size={16} color="#6b7285" />
            </button>
          </div>
        )}

        {/* ── Notificações ─────────────────────────────────────────────── */}
        <div className={S.bloco}>
          <div className={S.item} style={{ cursor: "default" }}>
            <span className={S.itemIconeOuro}><Bell size={15} /></span>
            <span className={S.itemLabel}>Notificações</span>
            <Toggle
              ligado={notificacoes}
              rotulo="Notificações do chat"
              onMudar={(v) => onSalvar({ notificacoes: v })}
            />
          </div>
          <button type="button" className={S.item} onClick={() => setModal("silencio")}>
            <span className={S.itemIconeOuro}><Moon size={15} /></span>
            <div className={S.itemLabel}>
              <div>Horário de silêncio</div>
              <div className={S.itemSub}>
                {silencioAtivo ? `${inicio} às ${fim}` : "Desligado"}
              </div>
            </div>
            <ChevronRight size={16} color="#6b7285" />
          </button>
          {/* Reativação de push negado — só aparece quando há o que reativar. */}
          {pushPerm && pushPerm !== "unavailable" && pushPerm !== "granted" && (
            <button type="button" className={S.item} onClick={reativarPush}>
              <span className={S.itemIconeOuro}><Bell size={15} /></span>
              <div className={S.itemLabel}>
                <div>Notificações do celular</div>
                <div className={S.itemSub}>
                  {pushPerm === "blocked"
                    ? "Bloqueadas — ative nos ajustes do sistema"
                    : "Desativadas — toque para ativar"}
                </div>
              </div>
              <ChevronRight size={16} color="#6b7285" />
            </button>
          )}
        </div>

        {/* ── Tema da conversa ─────────────────────────────────────────────
            Hoje só existe o tema escuro com o padrão de rabiscos. Os cartões
            saem de TEMAS (vitaPrefs), onde já há espaço comentado para temas
            com imagem .webp — quando os arquivos entrarem, aparecem aqui
            sozinhos, sem tocar neste componente. */}
        <div className={S.blocoTema}>
          <div className={S.temaTitulo}>Tema da conversa</div>
          <div className={S.temaGrade}>
            {TEMAS.map((t) => {
              const ativo = t.id === prefs.tema;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={S.temaItem}
                  onClick={() => mudarPref({ tema: t.id })}
                  aria-pressed={ativo}
                >
                  <span
                    className={ativo ? S.temaAmostraAtiva : S.temaAmostra}
                    style={t.imagem ? { backgroundImage: `url(${t.imagem})` } : undefined}
                  />
                  <span className={ativo ? S.temaLabelAtivo : S.temaLabel}>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Leitura ──────────────────────────────────────────────────── */}
        <div className={S.bloco}>
          <button type="button" className={S.item} onClick={() => setModal("fontes")}>
            <span className={S.itemIconeOuro}><Type size={15} /></span>
            <div className={S.itemLabel}>
              <div>Fontes</div>
              <div className={S.itemSub}>{fonte.label} · {tamanho.label}</div>
            </div>
            <ChevronRight size={16} color="#6b7285" />
          </button>
          <div className={S.item} style={{ cursor: "default" }}>
            <span className={S.itemIconeOuro}><Mic size={15} /></span>
            <div className={S.itemLabel}>
              <div>Transcrever áudios</div>
              <div className={S.itemSub}>Mostra o texto abaixo do áudio</div>
            </div>
            <Toggle
              ligado={transcrever}
              rotulo="Transcrever áudios"
              onMudar={(v) => onSalvar({ transcrever_audios: v })}
            />
          </div>
          <button type="button" className={S.item} onClick={() => setModal("idioma")}>
            <span className={S.itemIconeOuro}><Globe size={15} /></span>
            <span className={S.itemLabel}>Idioma</span>
            <span className={S.itemValor}>{idiomaLabel(idioma)}</span>
            <ChevronRight size={16} color="#6b7285" />
          </button>
        </div>

        {/* ── Conversa ─────────────────────────────────────────────────── */}
        <div className={S.bloco}>
          <button type="button" className={S.item} onClick={() => setModal("exportar")}>
            <span className={S.itemIcone}><Download size={15} /></span>
            <span className={S.itemLabel}>Exportar conversa</span>
            <ChevronRight size={16} color="#6b7285" />
          </button>
          <button type="button" className={S.item} onClick={() => setModal("limpar")}>
            <span className={S.itemIconePerigo}><Trash2 size={15} /></span>
            <span className={`${S.itemLabel} ${S.itemPerigo}`}>Limpar conversa</span>
          </button>
        </div>
      </div>

      {/* ── Horário de silêncio ──────────────────────────────────────────── */}
      {modal === "silencio" && (
        <ModalFolha
          titulo="Horário de silêncio"
          descricao="Nesse intervalo o VITA não envia notificação. As mensagens continuam chegando e ficam esperando no chat."
          onFechar={fechar}
          acoes={
            <button type="button" className={S.modalPrimario} onClick={fechar}>
              Concluído
            </button>
          }
        >
          <div className={S.modalLinha}>
            <span className={S.modalLinhaLabel}>Ativado</span>
            <Toggle
              ligado={silencioAtivo}
              rotulo="Ativar horário de silêncio"
              onMudar={(v) =>
                onSalvar({ silencio_ativo: v, silencio_inicio: inicio, silencio_fim: fim })
              }
            />
          </div>
          <div className={S.horaLinha}>
            <label className={S.horaCampo}>
              <span className={S.horaLabel}>Início</span>
              <input
                type="time"
                className={S.horaInput}
                value={inicio}
                disabled={!silencioAtivo}
                onChange={(e) => onSalvar({ silencio_inicio: e.target.value })}
              />
            </label>
            <label className={S.horaCampo}>
              <span className={S.horaLabel}>Fim</span>
              <input
                type="time"
                className={S.horaInput}
                value={fim}
                disabled={!silencioAtivo}
                onChange={(e) => onSalvar({ silencio_fim: e.target.value })}
              />
            </label>
          </div>
          {silencioAtivo && inicio === fim && (
            <div className={S.modalAviso}>
              Início e fim iguais desligam o silêncio na prática — escolha horários diferentes.
            </div>
          )}
        </ModalFolha>
      )}

      {/* ── Idioma ───────────────────────────────────────────────────────── */}
      {modal === "idioma" && (
        <ModalFolha
          titulo="Idioma"
          descricao="O VITA passa a responder no idioma escolhido."
          onFechar={fechar}
          acoes={
            <button type="button" className={S.modalPrimario} onClick={fechar}>
              Concluído
            </button>
          }
        >
          <ListaOpcoes
            opcoes={IDIOMAS}
            valor={idioma}
            onEscolher={(id) => onSalvar({ idioma: id })}
          />
        </ModalFolha>
      )}

      {/* ── Fontes ───────────────────────────────────────────────────────── */}
      {modal === "fontes" && (
        <ModalFolha
          titulo="Fontes"
          onFechar={fechar}
          acoes={
            <button type="button" className={S.modalPrimario} onClick={fechar}>
              Concluído
            </button>
          }
        >
          {/* Pré-visualização com a fonte E o tamanho aplicados de verdade — a
              escolha só faz sentido vendo a bolha como ela vai ficar. */}
          <div
            className={S.previewFonte}
            style={{ fontFamily: fonte.stack, fontSize: `${tamanho.px}px` }}
          >
            <div className={S.previewBolhaVita}>Bora treinar hoje? Preparei seu treino A.</div>
            <div className={S.previewBolhaUsuario}>Fechou, já tô indo pra academia</div>
          </div>

          <div className={S.modalSecao}>Fonte</div>
          <ListaOpcoes
            opcoes={FONTES}
            valor={prefs.fonte}
            onEscolher={(id) => mudarPref({ fonte: id })}
            estiloDoItem={(o) => ({ fontFamily: o.stack })}
          />

          <div className={S.modalSecao}>Tamanho</div>
          <ListaOpcoes
            opcoes={TAMANHOS}
            valor={prefs.tamanho}
            onEscolher={(id) => mudarPref({ tamanho: id })}
            estiloDoItem={(o) => ({ fontSize: `${o.px}px` })}
          />
        </ModalFolha>
      )}

      {/* ── Exportar (confirmação) ───────────────────────────────────────── */}
      {modal === "exportar" && (
        <ModalFolha
          titulo="Tem certeza que quer exportar toda conversa?"
          descricao="Um arquivo de texto com todas as mensagens será salvo no seu aparelho. Ele sai sem proteção — evite compartilhar."
          onFechar={fechar}
          acoes={
            <>
              <button
                type="button"
                className={S.modalPrimario}
                onClick={() => {
                  fechar();
                  onExportar?.();
                }}
              >
                Exportar conversa
              </button>
              <button type="button" className={S.confirmacaoCancelar} onClick={fechar}>
                Cancelar
              </button>
            </>
          }
        />
      )}

      {/* ── Limpar (confirmação) ─────────────────────────────────────────── */}
      {modal === "limpar" && (
        <ModalFolha
          titulo="Limpar esta conversa?"
          descricao="As mensagens saem do seu celular. Seus treinos, dietas e check-ins já salvos continuam no app."
          onFechar={fechar}
          acoes={
            <>
              <button
                type="button"
                className={S.confirmacaoPerigo}
                onClick={async () => {
                  fechar();
                  await onLimpar?.();
                }}
              >
                Limpar conversa
              </button>
              <button type="button" className={S.confirmacaoCancelar} onClick={fechar}>
                Cancelar
              </button>
            </>
          }
        />
      )}
    </>
  );
}
